import { Select } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { BudgetSuggestionRow } from 'src/Components/Expenses/BudgetSuggestionRow';
import { nextDraftKey } from 'src/Components/Expenses/BudgetWizard/BudgetWizard.utils';
import useConfirmRemoveWizardLine from 'src/Components/Expenses/BudgetWizard/useConfirmRemoveWizardLine';
import { BudgetCategory, placementOfCategory, WORKS_CATEGORIES } from 'src/Utils/budgetCategories';

import styles from './ProjectsChecklistStep.module.scss';
import { Props } from './ProjectsChecklistStep.types';

const ProjectsChecklistStep = ({ rows, onChange }: Props) => {
  const { t } = useTranslation();
  const confirmRemove = useConfirmRemoveWizardLine('projects');

  const patchRow = (key: string, patch: Partial<(typeof rows)[number]>) =>
    onChange(rows.map(row => (row.key === key ? { ...row, ...patch } : row)));

  // Picking a category adds the works line straight away — no button that stays inert.
  const addWorks = (value: string | null) => {
    if (!value) return;
    const category = value as BudgetCategory;
    const placement = placementOfCategory(category);
    onChange([
      ...rows,
      {
        key: nextDraftKey('project'),
        projectId: null,
        title: null,
        statusLabel: null,
        category,
        mainCategory: placement.mainCategory,
        subCategory: placement.subCategory,
        amount: 0,
        checked: true,
        isManual: true,
      },
    ]);
  };

  const options = WORKS_CATEGORIES.map(category => ({
    value: category,
    label: t(`expense.type.${category}`),
  })).sort((a, b) => a.label.localeCompare(b.label, 'fr'));

  return (
    <div className={styles.container}>
      <div className={styles.list}>
        {rows.length === 0 ? (
          <p className={styles.empty}>
            {t(
              'budgetV2.projects.empty',
              'Aucun projet « À voter » ni « Boîte à idées ». Ajoutez des travaux ci-dessous.'
            )}
          </p>
        ) : null}

        {rows.map(row => (
          <BudgetSuggestionRow
            key={row.key}
            checked={row.checked}
            onCheckedChange={checked => patchRow(row.key, { checked })}
            title={
              row.title ??
              (row.category
                ? t(`expense.type.${row.category}`)
                : t('budgetV2.projects.unnamed', 'Travaux'))
            }
            meta={
              row.statusLabel
                ? t('budgetV2.projects.from', 'Depuis « {{source}} »', {
                    source: t(`entityLinks.project.status.${row.statusLabel}`, row.statusLabel),
                  })
                : t('budgetV2.projects.manual', 'Travaux ajoutés à la main')
            }
            amountLabel={t('budgetV2.projects.budgetLabel', 'Budget')}
            amount={row.amount}
            onAmountChange={amount => patchRow(row.key, { amount })}
            onRemove={() => {
              const label =
                row.title ??
                (row.category
                  ? t(`expense.type.${row.category}`)
                  : t('budgetV2.projects.unnamed', 'Travaux'));
              confirmRemove(label, row.amount, () => onChange(rows.filter(r => r.key !== row.key)));
            }}
            removeLabel={t('budgetV2.removeLine', 'Retirer cette ligne')}
          />
        ))}
      </div>

      <div className={styles.addRow}>
        <Select
          className={styles.addSelect}
          size="md"
          searchable
          data={options}
          value={null}
          onChange={addWorks}
          placeholder={t('budgetV2.projects.addPlaceholder', 'Choisir un type de travaux')}
          label={t('budgetV2.projects.add', 'Ajouter des travaux non listés')}
        />
      </div>
    </div>
  );
};

export default ProjectsChecklistStep;
